import { pubText, pubPath, pubRich, pubFormat } from "../../i18n/publicTranslation";
import { KeyboardArrowRight, PlayArrowRounded, StopRounded } from '@mui/icons-material';
import { CircularProgress, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { homeVoices, voiceLibraryCount } from '../../data/homeVoices';
import { trackSwiperInteraction, trackVoicePlayEvent } from '../../firebase';
import FadeVideo from '../common/FadeVideo';
import HomeSectionDivider from '../common/HomeSectionDivider';
import PlaybackEqualizer from '../common/PlaybackEqualizer';
import './HomeVoiceShowcase.css';

type Preview = { id: string; status: 'loading' | 'playing' } | null;

function releaseAudio(audio: HTMLAudioElement) {
  audio.onplaying = audio.onwaiting = audio.onpause = audio.onended = audio.onerror = null;
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
}

export default function HomeVoiceShowcase() {
  const [preview, setPreview] = useState<Preview>(null);
  const [error, setError] = useState('');
  const activeAudio = useRef<HTMLAudioElement | null>(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(
    () => () => {
      if (activeAudio.current) releaseAudio(activeAudio.current);
      activeAudio.current = null;
    },
    []
  );

  const togglePreview = (voice: (typeof homeVoices)[number], index: number) => {
    const wasActive = preview?.id === voice.narrator;
    if (activeAudio.current) {
      const previous = activeAudio.current;
      activeAudio.current = null;
      releaseAudio(previous);
    }
    setPreview(null);
    setError('');
    trackSwiperInteraction('voice', 'click', index, voice.label);
    if (wasActive) return;

    const audio = new Audio(voice.audioFile);
    activeAudio.current = audio;
    setPreview({ id: voice.narrator, status: 'loading' });
    let tracked = false;
    const finish = (failed = false) => {
      if (activeAudio.current !== audio) return;
      activeAudio.current = null;
      releaseAudio(audio);
      setPreview(null);
      if (failed) setError(`Couldn’t play ${voice.label}. Please try again.`);
    };
    audio.onplaying = () => {
      if (activeAudio.current !== audio) return;
      setPreview({ id: voice.narrator, status: 'playing' });
      if (!tracked) {
        trackVoicePlayEvent(voice.label, voice.tone);
        tracked = true;
      }
    };
    audio.onwaiting = () => {
      if (activeAudio.current === audio) setPreview({ id: voice.narrator, status: 'loading' });
    };
    audio.onpause = () => finish();
    audio.onended = () => finish();
    audio.onerror = () => finish(true);
    audio.play().catch(() => finish(true));
  };

  return (
    <section className="home-voices" id="dialog-mode" aria-labelledby="home-voices-title">
      <HomeSectionDivider />
      <div className="home-voices-heading">
        <h2 id="home-voices-title">{pubRich(" Give Your Character a __SLOT_0__ ", [<span>{pubText("Voice")}</span>])}</h2>
        <p>{pubText(
          " Let our high-quality AI narrators tell the story with expressive voices and immersive sound effects. Choose from traditional storytelling or Dialog Mode to give characters their own unique voices. "
        )}</p>
      </div>
      <div className="home-voices-layout">
        <div className="home-voices-art" aria-hidden="true">
          <FadeVideo
            videoSrc={reducedMotion ? undefined : '/dialog.mov'}
            webmSrc={reducedMotion ? undefined : '/dialog.webm'}
            imageSrc={reducedMotion ? '/dialog.jpeg' : undefined}
            posterSrc="/dialog.jpeg"
            alt={pubText("")}
            maxWidth="600px"
            borderRadius="20px"
          />
        </div>
        <p className="home-voices-mode-description">{pubText(
          " Experience stories in two ways: Traditional narration where one voice tells the entire tale, or Dialog Mode where each character comes alive with their own distinct voice and personality. This adds another layer of immersion, to give more personality to your story. "
        )}</p>
        <div className="home-voices-library" id="ai-narrators">
          <div className="home-voices-library-heading">
            <h3>{pubText("Find a voice you love")}</h3>
            <span>{pubText(voiceLibraryCount)}{pubText(" voices")}</span>
          </div>
          <ul className="home-voice-grid">
            {pubText(homeVoices.map((voice, index) => {
              const active = preview?.id === voice.narrator;
              const loading = active && preview.status === 'loading';
              const playing = active && preview.status === 'playing';
              return (
                <li
                  key={voice.narrator}
                  className={`home-voice-card${active ? ' is-active' : ''}${playing ? ' is-playing' : ''}`}
                >
                  <button
                    type="button"
                    className="home-voice-portrait"
                    aria-label={pubFormat(
                      "__VALUE_0__ __VALUE_1__ voice preview",
                      [active ? 'Stop' : 'Play', voice.label]
                    )}
                    aria-pressed={active}
                    onClick={() => togglePreview(voice, index)}
                  >
                    <img src={voice.image} alt={pubText("")} width="124" height="124" loading="lazy" />
                    {pubText(active && (
                      <span className="home-voice-portrait-overlay">
                        {pubText(loading ? (
                          <CircularProgress size={25} color="inherit" aria-label={pubText("Loading preview")} />
                        ) : (
                          <PlaybackEqualizer />
                        ))}
                      </span>
                    ))}
                  </button>
                  <Link className="home-voice-profile" to={pubPath(`/narrators/${voice.narrator}`)}>
                    <strong>{pubText(voice.label)}</strong>
                  </Link>
                  <button
                    type="button"
                    className="home-voice-play"
                    aria-label={pubFormat(
                      "__VALUE_0__ __VALUE_1__ voice preview",
                      [active ? 'Stop' : 'Play', voice.label]
                    )}
                    aria-pressed={active}
                    onClick={() => togglePreview(voice, index)}
                  >
                    {pubText(active ? <StopRounded /> : <PlayArrowRounded />)}
                    <span>{pubText(loading ? 'Loading…' : active ? 'Stop voice' : 'Play voice')}</span>
                  </button>
                </li>
              );
            }))}
          </ul>
          {pubText(error && (
            <p className="home-voices-error" role="status">
              {pubText(error)}
            </p>
          ))}
          <Link to={pubPath("/narrators")} className="home-voices-explore">{pubRich(" Explore narrators __SLOT_0__ ", [<KeyboardArrowRight />])}</Link>
        </div>
      </div>
    </section>
  );
}
